import {
  type AnonymizeResult,
  DATA_SUBJECT_REQUEST_STATUS_LABELS,
  DATA_SUBJECT_REQUEST_CHANNEL_LABELS,
  DATA_SUBJECT_REQUEST_TYPE_LABELS,
  type DataSubjectRequest,
} from "@station/contracts";
import {
  Alert,
  Button,
  cn,
  ConfirmDialog,
  Dialog,
  FormField,
  SkeletonRows,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, CircleCheck, CircleX, Download, FileJson, Play, ShieldOff } from "lucide-react";
import { type ReactElement, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { MaskedNotice, PersonalValue } from "../../../../features/customers/personal-data.js";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import { Field, TableFrame } from "../../../../features/market/shared.js";
import {
  anonymizeConfirmation,
  requestActions,
  requestDeadline,
  validateResponse,
  workflowSteps,
} from "../../../../features/privacy/privacy-values.js";
import {
  usePrivacyPermissions,
  usePrivacyRequest,
  useRefreshPrivacy,
} from "../../../../features/privacy/queries.js";
import {
  DeadlinePhrase,
  PrivacyPage,
  privacyCrumb,
  RequestStatusBadge,
  Stamp,
} from "../../../../features/privacy/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";
import { useStepUp } from "../../../../lib/step-up.js";

export const Route = createFileRoute("/_app/kvkk/basvurular/$id")({ component: PrivacyRequestDetailRoute });

function PrivacyRequestDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = usePrivacyPermissions();
  const request = usePrivacyRequest(id);
  const title = request.data?.requestNo ?? t("privacy.requests.detailTitle");
  return (
    <PrivacyPage
      title={title}
      breadcrumb={[privacyCrumb(t("menu.privacyRequests"), "/kvkk/basvurular"), { label: title }]}
    >
      {!permissions.canRead ? null : request.error ? (
        <Alert tone="danger">{errorMessage(request.error)}</Alert>
      ) : !request.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <RequestDetail request={request.data} />
      )}
    </PrivacyPage>
  );
}

type ActionKey = "start" | "export";

/**
 * Başvuru ayrıntısı ve iş akışı. Tüm işleme adımları `privacy.request.manage` + STEP-UP ister (`useStepUp`: süre
 * dolduysa kimlik penceresi açılır, doğrulanınca işlem kendiliğinden yinelenir). Silme başvurusu anonimleştirilmeden
 * tamamlanamaz; anonimleştirme GERİ ALINAMAZ ve güçlü onay (müşteri kodu yazdırılır) ister.
 */
function RequestDetail({ request }: { request: DataSubjectRequest }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useRefreshPrivacy();
  const withStepUp = useStepUp();
  const permissions = usePrivacyPermissions();
  const completeHintId = useId();
  const actions = requestActions(request, permissions.canProcess);
  const deadline = requestDeadline(request);
  const open = request.status === "RECEIVED" || request.status === "IN_PROGRESS";
  const [actionError, setActionError] = useState<string | null>(null);
  const [anonymizing, setAnonymizing] = useState(false);
  const [anonymized, setAnonymized] = useState<AnonymizeResult | null>(null);
  const [resolving, setResolving] = useState<"complete" | "reject" | null>(null);
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);

  const store = async (next: DataSubjectRequest) => {
    queryClient.setQueryData(queryKeys.privacy("request", next.id), next);
    await refresh();
  };

  const act = useMutation({
    mutationFn: async (action: ActionKey) => {
      const params = { path: { id: request.id } };
      const body = { version: request.version };
      const response = await withStepUp(() =>
        action === "start"
          ? api.POST("/privacy/requests/{id}/start", { params, body })
          : api.POST("/privacy/requests/{id}/export", { params, body }),
      );
      return response.data!;
    },
    onMutate: () => setActionError(null),
    onSuccess: async (next, action) => {
      if (action === "export") setLink(null);
      await store(next);
      toast({ tone: "success", title: t(`privacy.requests.done.${action}`) });
    },
    onError: (caught) => setActionError(errorMessage(caught)),
  });

  const download = useMutation({
    mutationFn: async () =>
      (
        await withStepUp(() =>
          api.GET("/privacy/requests/{id}/export/download-link", { params: { path: { id: request.id } } }),
        )
      ).data!,
    onMutate: () => setActionError(null),
    onSuccess: (next) => setLink(next),
    onError: (caught) => setActionError(errorMessage(caught)),
  });

  const anonymize = useMutation({
    mutationFn: async () =>
      (
        await withStepUp(() =>
          api.POST("/privacy/requests/{id}/anonymize", {
            params: { path: { id: request.id } },
            body: { version: request.version },
          }),
        )
      ).data!,
    onMutate: () => setActionError(null),
    onSuccess: async (result) => {
      setAnonymized(result);
      await store(result.request);
      toast({ tone: "success", title: t("privacy.requests.done.anonymize") });
    },
  });

  // Başka bir başvuruya geçilince önceki başvurunun geçici sonuçları taşınmasın.
  useEffect(() => {
    setAnonymized(null);
    setLink(null);
    setActionError(null);
  }, [request.id]);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{DATA_SUBJECT_REQUEST_TYPE_LABELS[request.requestType]}</span>
        <RequestStatusBadge status={request.status} />
        {deadline ? <DeadlinePhrase info={deadline} showState /> : null}
      </div>

      <WorkflowBar request={request} />
      <MaskedNotice masked={request.personalDataMasked} />

      <Section title={t("privacy.requests.infoTitle")}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("privacy.requests.fields.requestType")}>
            {DATA_SUBJECT_REQUEST_TYPE_LABELS[request.requestType]}
          </Field>
          <Field label={t("privacy.requests.fields.channel")}>
            {DATA_SUBJECT_REQUEST_CHANNEL_LABELS[request.channel]}
          </Field>
          <Field label={t("privacy.requests.fields.receivedAt")}>
            <Stamp value={request.receivedAt} />
          </Field>
          <Field label={t("privacy.requests.fields.dueAt")}>
            <span className="tabular">{formatDate(request.dueAt)}</span>
          </Field>
          <Field label={t("privacy.requests.fields.subjectName")}>
            <PersonalValue value={request.subjectName} masked={request.personalDataMasked} />
          </Field>
          <Field label={t("privacy.requests.fields.subjectContact")}>
            <PersonalValue value={request.subjectContact} masked={request.personalDataMasked} />
          </Field>
          <Field label={t("privacy.requests.fields.identityVerified")}>
            {request.identityVerified ? t("common.yes") : t("common.no")}
          </Field>
          <Field label={t("privacy.requests.fields.customer")}>
            {request.customerCode ? (
              <span className="font-mono">{request.customerCode}</span>
            ) : (
              <span className="text-muted-foreground">{t("privacy.requests.notLinked")}</span>
            )}
          </Field>
        </dl>
        <div className="grid gap-1">
          <h3 className="text-xs text-muted-foreground">{t("privacy.requests.fields.description")}</h3>
          <div className="whitespace-pre-wrap break-words text-sm">
            <PersonalValue value={request.description} masked={request.personalDataMasked} />
          </div>
        </div>
      </Section>

      {open ? (
        <Section title={t("privacy.requests.actionsTitle")}>
          {permissions.canProcess ? (
            <>
              <p className="text-sm text-muted-foreground">{t("privacy.requests.stepUpHint")}</p>
              {actionError ? <Alert tone="danger">{actionError}</Alert> : null}
              <div className="flex flex-wrap gap-2">
                {actions.start ? (
                  <Button
                    loading={act.isPending && act.variables === "start"}
                    onClick={() => act.mutate("start")}
                  >
                    <Play aria-hidden="true" />
                    {t("privacy.requests.start")}
                  </Button>
                ) : null}
                {actions.export ? (
                  <Button
                    variant="outline"
                    loading={act.isPending && act.variables === "export"}
                    onClick={() => act.mutate("export")}
                  >
                    <FileJson aria-hidden="true" />
                    {request.exportFileId ? t("privacy.requests.reexport") : t("privacy.requests.export")}
                  </Button>
                ) : null}
                {actions.download ? (
                  <Button variant="outline" loading={download.isPending} onClick={() => download.mutate()}>
                    <Download aria-hidden="true" />
                    {t("privacy.requests.download")}
                  </Button>
                ) : null}
                {actions.anonymize ? (
                  <Button variant="destructive" onClick={() => setAnonymizing(true)}>
                    <ShieldOff aria-hidden="true" />
                    {t("privacy.requests.anonymize")}
                  </Button>
                ) : null}
                {actions.complete ? (
                  <Button
                    disabled={actions.completeBlocked !== null}
                    aria-describedby={actions.completeBlocked ? completeHintId : undefined}
                    onClick={() => setResolving("complete")}
                  >
                    <CircleCheck aria-hidden="true" />
                    {t("privacy.requests.complete")}
                  </Button>
                ) : null}
                {actions.reject ? (
                  <Button variant="outline" onClick={() => setResolving("reject")}>
                    <CircleX aria-hidden="true" />
                    {t("privacy.requests.reject")}
                  </Button>
                ) : null}
              </div>
              {actions.completeBlocked ? (
                <p id={completeHintId} className="text-sm text-muted-foreground">
                  {t(`privacy.requests.completeBlocked.${actions.completeBlocked}`)}
                </p>
              ) : null}
              {request.status === "IN_PROGRESS" && request.customerProfileId === null ? (
                <Alert tone="info">{t("privacy.requests.unlinkedHint")}</Alert>
              ) : null}
            </>
          ) : (
            <Alert tone="info">{t("privacy.requests.noProcessPermission")}</Alert>
          )}
        </Section>
      ) : null}

      {!open && actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {link ? (
        <Alert tone="success" title={t("privacy.requests.downloadReady")}>
          <span className="grid gap-1">
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="font-medium underline underline-offset-4"
            >
              {t("privacy.requests.downloadOpen")}
            </a>
            <span>{t("privacy.requests.downloadExpires", { time: formatDateTime(link.expiresAt) })}</span>
          </span>
        </Alert>
      ) : null}

      {anonymized ? <AnonymizeResultSection result={anonymized} /> : null}

      <Section title={t("privacy.requests.recordTitle")}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("privacy.requests.fields.export")}>
            <span className="grid gap-0.5" data-testid="request-export-field">
              {request.exportFileId ? (
                <>
                  <span>{t("privacy.requests.exportReady")}</span>
                  {request.exportedAt ? <Stamp value={request.exportedAt} /> : null}
                </>
              ) : (
                t("privacy.requests.exportNone")
              )}
            </span>
          </Field>
          <Field label={t("privacy.requests.fields.anonymizedAt")}>
            <Stamp value={request.anonymizedAt} />
          </Field>
          <Field label={t("privacy.requests.fields.completedAt")}>
            <Stamp value={request.completedAt} />
          </Field>
          <Field label={t("privacy.requests.fields.createdAt")}>
            <Stamp value={request.createdAt} />
          </Field>
        </dl>
        {request.response ? (
          <div className="grid gap-1">
            <h3 className="text-xs text-muted-foreground">{t("privacy.requests.fields.response")}</h3>
            <p className="whitespace-pre-wrap break-words text-sm">{request.response}</p>
          </div>
        ) : null}
        {!open && actions.download ? (
          <div>
            <Button variant="outline" loading={download.isPending} onClick={() => download.mutate()}>
              <Download aria-hidden="true" />
              {t("privacy.requests.download")}
            </Button>
          </div>
        ) : null}
      </Section>

      {actions.anonymize ? (
        <ConfirmDialog
          open={anonymizing}
          onOpenChange={setAnonymizing}
          tone="danger"
          title={t("privacy.requests.anonymizeTitle")}
          description={t("privacy.requests.anonymizeDescription")}
          confirmationText={anonymizeConfirmation(request)}
          confirmLabel={t("privacy.requests.anonymize")}
          onConfirm={async () => {
            try {
              await anonymize.mutateAsync();
            } catch (caught) {
              setActionError(errorMessage(caught));
            }
          }}
        >
          <ul className="grid list-disc gap-1 ps-5 text-sm">
            <li>{t("privacy.requests.anonymizeEffects.profile")}</li>
            <li>{t("privacy.requests.anonymizeEffects.vehicles")}</li>
            <li>{t("privacy.requests.anonymizeEffects.loyalty")}</li>
            <li>{t("privacy.requests.anonymizeEffects.legal")}</li>
            <li>{t("privacy.requests.anonymizeEffects.irreversible")}</li>
          </ul>
        </ConfirmDialog>
      ) : null}

      <ResolveDialog
        request={request}
        mode={resolving}
        onClose={() => setResolving(null)}
        onResolved={async (next) => {
          await store(next);
          toast({
            tone: "success",
            title: t(`privacy.requests.done.${next.status === "REJECTED" ? "reject" : "complete"}`),
          });
        }}
      />
    </div>
  );
}

/** Alındı → İşlemde → Tamamlandı / Reddedildi. Geçerli adım `aria-current="step"`; durum METİNLE de yazılır. */
function WorkflowBar({ request }: { request: DataSubjectRequest }): ReactElement {
  const { t } = useTranslation();
  const steps = workflowSteps(request.status);
  return (
    <ol
      aria-label={t("privacy.requests.workflow")}
      className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm"
    >
      {steps.map((step, index) => (
        <li
          key={step.status}
          className="flex items-center gap-2"
          {...(step.state === "current" ? { "aria-current": "step" as const } : {})}
        >
          <span
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
              step.state === "done" && "border-success bg-success-subtle text-success",
              step.state === "current" && "border-primary bg-primary text-primary-foreground",
              (step.state === "upcoming" || step.state === "skipped") && "text-muted-foreground",
            )}
            aria-hidden="true"
          >
            {step.state === "done" ? <Check className="size-3.5" /> : index + 1}
          </span>
          <span
            className={cn(
              step.state === "current" ? "font-semibold" : "text-muted-foreground",
              step.state === "skipped" && "line-through",
            )}
          >
            {DATA_SUBJECT_REQUEST_STATUS_LABELS[step.status]}
          </span>
          <span className="sr-only">({t(`privacy.requests.stepState.${step.state}`)})</span>
          {index < steps.length - 1 ? (
            <span className="mx-1 h-px w-6 bg-border sm:w-10" aria-hidden="true" />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function AnonymizeResultSection({ result }: { result: AnonymizeResult }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("privacy.requests.anonymizeResultTitle")}>
      <p className="text-sm" data-testid="anonymize-affected">
        {t("privacy.requests.anonymizeAffected", { affected: formatCount(result.affectedRecords) })}
      </p>
      <TableFrame label={t("privacy.requests.anonymizeModules")}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("privacy.requests.fields.module")}</Th>
            <Th align="right">{t("privacy.requests.fields.affected")}</Th>
          </tr>
        </thead>
        <tbody>
          {result.modules.map((module) => (
            <tr key={module.module} className="border-t">
              <td className="px-3 py-2">
                {t(`privacy.requests.modules.${module.module}`, { defaultValue: module.module })}
              </td>
              <td className="px-3 py-2 text-right tabular">{formatCount(module.affected)}</td>
            </tr>
          ))}
        </tbody>
      </TableFrame>
      <p className="text-xs text-muted-foreground">{t("privacy.requests.anonymizeLegalNote")}</p>
    </Section>
  );
}

/**
 * Tamamla / reddet: yanıt metni zorunludur (en az 5 karakter). Silme başvurusu anonimleştirilmeden tamamlanamaz —
 * düğme zaten pasiftir; sunucu yine de 409 dönerse ileti buna bağlanır.
 */
function ResolveDialog({
  request,
  mode,
  onClose,
  onResolved,
}: {
  request: DataSubjectRequest;
  mode: "complete" | "reject" | null;
  onClose: () => void;
  onResolved: (request: DataSubjectRequest) => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const withStepUp = useStepUp();
  const [response, setResponse] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === null) return;
    setResponse("");
    setFieldError(null);
    setFormError(null);
  }, [mode]);

  const resolve = useMutation({
    mutationFn: async (target: "complete" | "reject") => {
      const params = { path: { id: request.id } };
      const body = { version: request.version, response: response.trim() };
      const result = await withStepUp(() =>
        target === "complete"
          ? api.POST("/privacy/requests/{id}/complete", { params, body })
          : api.POST("/privacy/requests/{id}/reject", { params, body }),
      );
      return result.data!;
    },
    onSuccess: async (next) => {
      await onResolved(next);
      onClose();
    },
    onError: (caught, target) => {
      if (target === "complete" && isApiError(caught, "CONFLICT") && request.requestType === "ERASURE")
        return setFormError(t("privacy.requests.completeBlocked.notAnonymized"));
      if (isApiError(caught, "VERSION_CONFLICT")) return setFormError(t("privacy.requests.versionConflict"));
      setFormError(errorMessage(caught));
    },
  });

  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(next) => {
        if (!next && !resolve.isPending) onClose();
      }}
      title={mode === "reject" ? t("privacy.requests.rejectTitle") : t("privacy.requests.completeTitle")}
      description={
        mode === "reject"
          ? t("privacy.requests.rejectDescription")
          : t("privacy.requests.completeDescription")
      }
      dismissible={!resolve.isPending}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!mode) return;
          setFormError(null);
          const code = validateResponse(response);
          setFieldError(code ? t(`privacy.validation.${code}`) : null);
          if (!code) resolve.mutate(mode);
        }}
      >
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField
          label={
            mode === "reject"
              ? t("privacy.requests.fields.rejectResponse")
              : t("privacy.requests.fields.response")
          }
          description={t("privacy.requests.fields.responseHint")}
          error={fieldError ?? undefined}
          required
        >
          {(control) => (
            <Textarea
              {...control}
              rows={5}
              maxLength={4000}
              value={response}
              onChange={(event) => setResponse(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={resolve.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={mode === "reject" ? "destructive" : "primary"}
            loading={resolve.isPending}
          >
            {mode === "reject" ? t("privacy.requests.reject") : t("privacy.requests.complete")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
